/**
 * Users Management - Admin only
 * Create resellers, set credits, enable/disable, change password,
 * impersonate ("Login as reseller"), set restrictions
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listUsers,
  createUser,
  updateUser,
  changeUserPassword,
  deleteUser,
  getSettings,
  addCredits,
  toggleUnlimitedCredits,
  impersonateUser,
} from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Trash2,
  KeyRound,
  ShieldCheck,
  UserCog,
  Users as UsersIcon,
  Coins,
  Infinity as InfinityIcon,
  LogIn,
  MinusCircle,
  PlusCircle,
} from "lucide-react";

const USER_TIDS = {
  btnNewUser: "btn-new-user",
  newUserDialog: "new-user-dialog",
  newUsername: "new-username",
  newPassword: "new-password",
  newDisplayName: "new-display-name",
  newRole: "new-role",
  newMaxKeys: "new-max-keys",
  newMaxPlaylists: "new-max-playlists",
  newCanManageDevices: "new-can-manage-devices",
  newCanSendNotifs: "new-can-send-notifs",
  newCredits: "new-credits",
  newUnlimitedCredits: "new-unlimited-credits",
  newUserSave: "new-user-save",
  userRow: (id) => `user-row-${id}`,
  btnToggleUser: (id) => `btn-toggle-user-${id}`,
  btnChangePassword: (id) => `btn-change-password-${id}`,
  btnEditUser: (id) => `btn-edit-user-${id}`,
  btnDeleteUser: (id) => `btn-delete-user-${id}`,
  btnImpersonate: (id) => `btn-impersonate-${id}`,
  btnAddCredits: (id) => `btn-add-credits-${id}`,
  btnToggleUnlimited: (id) => `btn-toggle-unlimited-${id}`,
  passwordDialog: "password-dialog",
  passwordInput: "password-input",
  passwordSave: "password-save",
  creditsDialog: "credits-dialog",
  creditsInput: "credits-input",
  creditsNote: "credits-note",
  creditsAddBtn: "credits-add-btn",
  creditsSubtractBtn: "credits-subtract-btn",
  editDialog: "edit-user-dialog",
  editSave: "edit-user-save",
};

export default function Users() {
  const { user: me, startImpersonation } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaults, setDefaults] = useState(null);

  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState(emptyNewForm());
  const [creating, setCreating] = useState(false);

  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [creditsTarget, setCreditsTarget] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState("");
  const [creditsNote, setCreditsNote] = useState("");
  const [savingCredits, setSavingCredits] = useState(false);

  function emptyNewForm() {
    return {
      username: "",
      password: "",
      display_name: "",
      email: "",
      role: "reseller",
      max_keys: "",
      max_playlists: "",
      can_manage_devices: true,
      can_send_notifications: false,
      credits: 0,
      unlimited_credits: false,
    };
  }

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([listUsers(), getSettings()]);
      setUsers(u);
      setDefaults(s);
    } catch (e) {
      toast({ title: "Failed to load", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    if (newOpen && defaults) {
      setNewForm({
        ...emptyNewForm(),
        max_keys: defaults.default_reseller_max_keys ?? "",
        max_playlists: defaults.default_reseller_max_playlists ?? "",
        can_manage_devices: defaults.default_reseller_can_manage_devices,
        can_send_notifications: defaults.default_reseller_can_send_notifications,
      });
    }
  }, [newOpen, defaults]);

  const handleCreate = async () => {
    if (!newForm.username || !newForm.password) {
      toast({ title: "Username and password required", variant: "destructive" });
      return;
    }
    setCreating(true);
    try {
      await createUser({
        username: newForm.username,
        password: newForm.password,
        display_name: newForm.display_name || newForm.username,
        email: newForm.email || null,
        role: newForm.role,
        max_keys: newForm.max_keys === "" ? null : parseInt(newForm.max_keys, 10),
        max_playlists: newForm.max_playlists === "" ? null : parseInt(newForm.max_playlists, 10),
        can_manage_devices: newForm.can_manage_devices,
        can_send_notifications: newForm.can_send_notifications,
        credits: parseInt(newForm.credits, 10) || 0,
        unlimited_credits: newForm.unlimited_credits,
      });
      toast({ title: "User created" });
      setNewOpen(false);
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      toast({ title: u.is_active ? "User disabled" : "User enabled" });
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user '${u.username}'?`)) return;
    try {
      await deleteUser(u.id);
      toast({ title: "User deleted" });
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 3) {
      toast({ title: "Password too short (min 3)", variant: "destructive" });
      return;
    }
    try {
      await changeUserPassword(passwordTarget.id, newPassword);
      toast({ title: "Password changed" });
      setPasswordTarget(null);
      setNewPassword("");
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({
      display_name: u.display_name || "",
      email: u.email || "",
      role: u.role,
      max_keys: u.max_keys ?? "",
      max_playlists: u.max_playlists ?? "",
      can_manage_devices: u.can_manage_devices,
      can_send_notifications: u.can_send_notifications,
      unlimited_credits: u.unlimited_credits,
    });
  };

  const handleSaveEdit = async () => {
    try {
      await updateUser(editTarget.id, {
        display_name: editForm.display_name,
        email: editForm.email || "",
        role: editForm.role,
        max_keys: editForm.max_keys === "" ? null : parseInt(editForm.max_keys, 10),
        max_playlists:
          editForm.max_playlists === "" ? null : parseInt(editForm.max_playlists, 10),
        can_manage_devices: editForm.can_manage_devices,
        can_send_notifications: editForm.can_send_notifications,
        unlimited_credits: editForm.unlimited_credits,
      });
      toast({ title: "User updated" });
      setEditTarget(null);
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  const openCredits = (u) => {
    setCreditsTarget(u);
    setCreditsAmount("");
    setCreditsNote("");
  };

  const handleAddCredits = async (sign) => {
    const amt = parseInt(creditsAmount, 10);
    if (!amt || amt < 1) {
      toast({ title: "Enter a positive amount", variant: "destructive" });
      return;
    }
    setSavingCredits(true);
    try {
      await addCredits(creditsTarget.id, sign * amt, creditsNote);
      toast({
        title: sign > 0 ? `Added ${amt} credits` : `Removed ${amt} credits`,
      });
      setCreditsTarget(null);
      setCreditsAmount("");
      setCreditsNote("");
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setSavingCredits(false);
    }
  };

  const handleToggleUnlimited = async (u) => {
    try {
      await toggleUnlimitedCredits(u.id, !u.unlimited_credits);
      toast({
        title: u.unlimited_credits
          ? "Unlimited credits disabled"
          : "Unlimited credits enabled",
      });
      refresh();
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  const handleImpersonate = async (u) => {
    if (!window.confirm(`Sign in as ${u.username}? You can return to admin from the banner.`)) {
      return;
    }
    try {
      const data = await impersonateUser(u.id);
      startImpersonation(data.access_token, data.user);
      toast({ title: `Signed in as ${u.username}` });
      navigate("/admin", { replace: true });
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-slate-600">
          Manage admins and resellers. Add credits, set restrictions, or sign in as a reseller.
        </p>
        <Button
          data-testid={USER_TIDS.btnNewUser}
          onClick={() => setNewOpen(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New User
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <UsersIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No users yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Restrictions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={USER_TIDS.userRow(u.id)}>
                    <TableCell>
                      <div className="font-mono font-semibold">
                        {u.username}
                        {u.id === me.id && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            you
                          </Badge>
                        )}
                      </div>
                      {u.display_name && u.display_name !== u.username && (
                        <div className="text-xs text-slate-500">{u.display_name}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          u.role === "admin"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-sky-100 text-sky-700"
                        }
                        variant="outline"
                      >
                        {u.role === "admin" ? (
                          <ShieldCheck className="w-3 h-3 mr-1" />
                        ) : (
                          <UserCog className="w-3 h-3 mr-1" />
                        )}
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {u.role === "admin" ? (
                        <span className="text-xs text-slate-400">N/A</span>
                      ) : u.unlimited_credits ? (
                        <div className="flex items-center gap-1 text-emerald-700 text-sm font-semibold">
                          <InfinityIcon className="w-3.5 h-3.5" />
                          Unlimited
                        </div>
                      ) : (
                        <div
                          className={`text-sm font-semibold ${
                            (u.credits ?? 0) <= 0
                              ? "text-red-600"
                              : (u.credits ?? 0) < 5
                              ? "text-amber-600"
                              : "text-slate-900"
                          }`}
                        >
                          <Coins className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                          {u.credits ?? 0}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {u.role === "reseller" ? (
                        <div className="space-y-0.5">
                          <div className="text-slate-500">
                            Keys cap: {u.max_keys ?? "∞"} · Playlists: {u.max_playlists ?? "∞"}
                          </div>
                          <div className="text-slate-400">
                            {u.can_manage_devices ? "✓ devices" : "✗ devices"} ·{" "}
                            {u.can_send_notifications ? "✓ notifs" : "✗ notifs"}
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-400">Full access</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        data-testid={USER_TIDS.btnToggleUser(u.id)}
                        onClick={() => handleToggleActive(u)}
                        disabled={u.id === me.id}
                        className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                          u.is_active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-200 text-slate-600"
                        } ${u.id === me.id ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:opacity-80"}`}
                      >
                        {u.is_active ? "Enabled" : "Disabled"}
                      </button>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {new Date(u.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end flex-wrap">
                        {u.role === "reseller" && (
                          <>
                            <Button
                              data-testid={USER_TIDS.btnAddCredits(u.id)}
                              size="sm"
                              variant="outline"
                              onClick={() => openCredits(u)}
                              title="Add or remove credits"
                            >
                              <Coins className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              data-testid={USER_TIDS.btnImpersonate(u.id)}
                              size="sm"
                              variant="outline"
                              onClick={() => handleImpersonate(u)}
                              title="Sign in as this reseller"
                            >
                              <LogIn className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        <Button
                          data-testid={USER_TIDS.btnEditUser(u.id)}
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(u)}
                          title="Edit user"
                        >
                          <UserCog className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          data-testid={USER_TIDS.btnChangePassword(u.id)}
                          size="sm"
                          variant="outline"
                          onClick={() => setPasswordTarget(u)}
                          title="Change password"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          data-testid={USER_TIDS.btnDeleteUser(u.id)}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(u)}
                          disabled={u.id === me.id}
                          className="text-red-600 hover:bg-red-50 disabled:opacity-40"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New User Dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid={USER_TIDS.newUserDialog} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Create an admin or reseller. Resellers see only their own keys/playlists. 1 credit = 1 activation key.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Username *</Label>
                <Input
                  data-testid={USER_TIDS.newUsername}
                  value={newForm.username}
                  onChange={(e) => setNewForm({ ...newForm, username: e.target.value })}
                  placeholder="reseller1"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <Input
                  data-testid={USER_TIDS.newPassword}
                  type="password"
                  value={newForm.password}
                  onChange={(e) => setNewForm({ ...newForm, password: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Display Name</Label>
              <Input
                data-testid={USER_TIDS.newDisplayName}
                value={newForm.display_name}
                onChange={(e) => setNewForm({ ...newForm, display_name: e.target.value })}
                placeholder="Optional friendly name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="new-email"
                type="email"
                value={newForm.email}
                onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
                placeholder="Used for password reset (optional)"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={newForm.role}
                onValueChange={(v) => setNewForm({ ...newForm, role: v })}
              >
                <SelectTrigger data-testid={USER_TIDS.newRole}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reseller">Reseller (limited access)</SelectItem>
                  <SelectItem value="admin">Admin (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newForm.role === "reseller" && (
              <>
                {/* Credits */}
                <div className="bg-orange-50/50 border border-orange-200 rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm flex items-center gap-1.5">
                      <Coins className="w-4 h-4 text-orange-600" />
                      Credits (1 credit = 1 activation key)
                    </Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      data-testid={USER_TIDS.newCredits}
                      type="number"
                      min="0"
                      value={newForm.credits}
                      onChange={(e) => setNewForm({ ...newForm, credits: e.target.value })}
                      placeholder="0"
                      disabled={newForm.unlimited_credits}
                      className="bg-white max-w-[140px]"
                    />
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        data-testid={USER_TIDS.newUnlimitedCredits}
                        checked={newForm.unlimited_credits}
                        onCheckedChange={(v) =>
                          setNewForm({ ...newForm, unlimited_credits: !!v })
                        }
                      />
                      <InfinityIcon className="w-3.5 h-3.5" />
                      Unlimited credits
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Max Keys cap (optional)</Label>
                    <Input
                      data-testid={USER_TIDS.newMaxKeys}
                      type="number"
                      min="0"
                      value={newForm.max_keys}
                      onChange={(e) => setNewForm({ ...newForm, max_keys: e.target.value })}
                      placeholder="No cap"
                    />
                  </div>
                  <div>
                    <Label>Max Playlists</Label>
                    <Input
                      data-testid={USER_TIDS.newMaxPlaylists}
                      type="number"
                      min="0"
                      value={newForm.max_playlists}
                      onChange={(e) =>
                        setNewForm({ ...newForm, max_playlists: e.target.value })
                      }
                      placeholder="No cap"
                    />
                  </div>
                </div>
                <div className="space-y-2 bg-slate-50 p-3 rounded-md">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      data-testid={USER_TIDS.newCanManageDevices}
                      checked={newForm.can_manage_devices}
                      onCheckedChange={(v) =>
                        setNewForm({ ...newForm, can_manage_devices: !!v })
                      }
                    />
                    Can manage devices (block / unblock / delete)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      data-testid={USER_TIDS.newCanSendNotifs}
                      checked={newForm.can_send_notifications}
                      onCheckedChange={(v) =>
                        setNewForm({ ...newForm, can_send_notifications: !!v })
                      }
                    />
                    Can send notifications
                  </label>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={USER_TIDS.newUserSave}
              onClick={handleCreate}
              disabled={creating}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {creating ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={!!passwordTarget} onOpenChange={(o) => !o && setPasswordTarget(null)}>
        <DialogContent data-testid={USER_TIDS.passwordDialog}>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Set a new password for{" "}
              <span className="font-mono font-semibold">{passwordTarget?.username}</span>
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>New Password</Label>
            <Input
              data-testid={USER_TIDS.passwordInput}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordTarget(null)}>
              Cancel
            </Button>
            <Button
              data-testid={USER_TIDS.passwordSave}
              onClick={handleChangePassword}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Update Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid={USER_TIDS.editDialog} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              {editTarget?.username} ·{" "}
              <span className="capitalize">{editTarget?.role}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Display Name</Label>
              <Input
                value={editForm.display_name || ""}
                onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="edit-email"
                type="email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="Used for password reset (optional)"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={editForm.role}
                onValueChange={(v) => setEditForm({ ...editForm, role: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reseller">Reseller</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.role === "reseller" && (
              <>
                <div className="flex items-center justify-between bg-orange-50/50 border border-orange-200 px-3 py-2 rounded-md">
                  <Label className="text-sm flex items-center gap-1.5">
                    <InfinityIcon className="w-4 h-4 text-orange-600" />
                    Unlimited credits
                  </Label>
                  <Switch
                    checked={editForm.unlimited_credits || false}
                    onCheckedChange={(v) =>
                      setEditForm({ ...editForm, unlimited_credits: v })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Max Keys cap</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editForm.max_keys ?? ""}
                      onChange={(e) => setEditForm({ ...editForm, max_keys: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Max Playlists</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editForm.max_playlists ?? ""}
                      onChange={(e) =>
                        setEditForm({ ...editForm, max_playlists: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2 bg-slate-50 p-3 rounded-md">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={editForm.can_manage_devices}
                      onCheckedChange={(v) =>
                        setEditForm({ ...editForm, can_manage_devices: !!v })
                      }
                    />
                    Can manage devices
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={editForm.can_send_notifications}
                      onCheckedChange={(v) =>
                        setEditForm({ ...editForm, can_send_notifications: !!v })
                      }
                    />
                    Can send notifications
                  </label>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button
              data-testid={USER_TIDS.editSave}
              onClick={handleSaveEdit}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credits Dialog */}
      <Dialog open={!!creditsTarget} onOpenChange={(o) => !o && setCreditsTarget(null)}>
        <DialogContent data-testid={USER_TIDS.creditsDialog} className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Credits</DialogTitle>
            <DialogDescription>
              <span className="font-mono font-semibold">{creditsTarget?.username}</span>{" "}
              currently has{" "}
              {creditsTarget?.unlimited_credits ? (
                <span className="text-emerald-700 font-semibold">unlimited credits</span>
              ) : (
                <span className="font-semibold">{creditsTarget?.credits ?? 0} credits</span>
              )}
              .
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount</Label>
              <Input
                data-testid={USER_TIDS.creditsInput}
                type="number"
                min="1"
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(e.target.value)}
                placeholder="e.g. 10"
                autoFocus
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input
                data-testid={USER_TIDS.creditsNote}
                value={creditsNote}
                onChange={(e) => setCreditsNote(e.target.value)}
                placeholder="e.g. Payment received"
              />
            </div>
            <div className="flex items-center justify-between gap-2 bg-slate-50 px-3 py-2 rounded-md">
              <div>
                <Label className="text-sm">Unlimited credits</Label>
                <p className="text-xs text-slate-500">
                  Reseller never runs out of credits
                </p>
              </div>
              <Switch
                data-testid={USER_TIDS.btnToggleUnlimited(creditsTarget?.id || "")}
                checked={creditsTarget?.unlimited_credits || false}
                onCheckedChange={() => creditsTarget && handleToggleUnlimited(creditsTarget)}
              />
            </div>
          </div>
          <DialogFooter className="grid grid-cols-2 gap-2 sm:flex">
            <Button
              data-testid={USER_TIDS.creditsSubtractBtn}
              variant="outline"
              onClick={() => handleAddCredits(-1)}
              disabled={savingCredits}
              className="text-red-600"
            >
              <MinusCircle className="w-4 h-4 mr-1" />
              Subtract
            </Button>
            <Button
              data-testid={USER_TIDS.creditsAddBtn}
              onClick={() => handleAddCredits(1)}
              disabled={savingCredits}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <PlusCircle className="w-4 h-4 mr-1" />
              Add credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
